import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectTheme, setTheme, toggleTheme } from 'store/slices/user.slice';

// Component scope - Constants and static data
const THEME_STORAGE_KEY = 'theme';
const DEFAULT_THEME = 'dark';
const SUPPORTED_THEMES = ['light', 'dark'];

/**
 * Custom hook for theme management
 * @returns {Object} Theme state and methods
 */
const useTheme = () => {
  // States, refs, selectors, RTK Query hooks
  const dispatch = useDispatch();
  const currentTheme = useSelector(selectTheme);
  const [isSystemTheme, setIsSystemTheme] = useState(false);

  // Other functions and logic
  const applyTheme = useCallback((theme) => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // useEffect hooks
  useEffect(() => {
    // Initialize theme from localStorage or system preference
    const initializeTheme = () => {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      
      if (savedTheme && SUPPORTED_THEMES.includes(savedTheme)) {
        dispatch(setTheme(savedTheme));
        applyTheme(savedTheme);
      } else {
        // Use default theme instead of system preference
        dispatch(setTheme(DEFAULT_THEME));
        applyTheme(DEFAULT_THEME);
        setIsSystemTheme(false);
      }
    };

    initializeTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e) => {
      if (isSystemTheme) {
        const newTheme = e.matches ? 'dark' : 'light';
        dispatch(setTheme(newTheme));
        applyTheme(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [dispatch, isSystemTheme, applyTheme]);

  const handleSetTheme = useCallback((theme) => {
    if (SUPPORTED_THEMES.includes(theme)) {
      dispatch(setTheme(theme));
      applyTheme(theme);
      localStorage.setItem(THEME_STORAGE_KEY, theme);
      setIsSystemTheme(false);
    }
  }, [dispatch, applyTheme]);

  const handleToggleTheme = useCallback(() => {
    dispatch(toggleTheme());
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    applyTheme(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    setIsSystemTheme(false);
  }, [dispatch, currentTheme, applyTheme]);

  const resetToSystemTheme = useCallback(() => {
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const systemTheme = systemPrefersDark ? 'dark' : 'light';
    
    dispatch(setTheme(systemTheme));
    applyTheme(systemTheme);
    localStorage.removeItem(THEME_STORAGE_KEY);
    setIsSystemTheme(true);
  }, [dispatch, applyTheme]);

  const isDarkMode = currentTheme === 'dark';
  const isLightMode = currentTheme === 'light';

  return {
    // State
    theme: currentTheme,
    isDarkMode,
    isLightMode,
    isSystemTheme,
    supportedThemes: SUPPORTED_THEMES,
    
    // Methods
    setTheme: handleSetTheme,
    toggleTheme: handleToggleTheme,
    resetToSystemTheme,
  };
};

export default useTheme;

